import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, API, buildWhatsAppUrl, fmtItalianDate, fmtItalianTime } from "@/lib/api";
import Header from "@/components/app/Header";
import { CheckCircle2, Calendar, MapPin, MessageCircle, Download, Home, Hourglass, XCircle } from "lucide-react";

function fmtDate(iso) { return fmtItalianDate(iso, { withYear: true }); }
function fmtTime(iso) { return fmtItalianTime(iso); }

export default function Confirmation() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/appointments/${id}`).then((r) => setData(r.data)).catch((e) => setErr("Prenotazione non trovata"));
  }, [id]);

  if (err) return (
    <div className="min-h-screen"><Header /><main className="max-w-2xl mx-auto px-5 pt-10"><p className="text-[#B85C5C]">{err}</p></main></div>
  );
  if (!data) return (
    <div className="min-h-screen"><Header /><main className="max-w-2xl mx-auto px-5 pt-10"><p className="text-[#5C7066]">Caricamento…</p></main></div>
  );

  const { appointment, studio, settings } = data;
  const isPending = appointment.status === "pending_confirmation";
  const isRejected = appointment.status === "cancelled";
  const wmsg = `Ciao, ho appena prenotato un appuntamento il ${fmtDate(appointment.start)} alle ${fmtTime(appointment.start)} presso ${studio?.name}.`;

  return (
    <div className="min-h-screen">
      <Header minimal />
      <main className="max-w-2xl mx-auto px-5 md:px-8 pt-10 pb-24">
        <div className="fade-up text-center" data-testid="confirmation-screen">
          {isRejected ? (
            <>
              <div className="mx-auto h-20 w-20 rounded-full bg-[#B85C5C]/15 grid place-items-center">
                <XCircle size={42} className="text-[#B85C5C]" strokeWidth={1.6} />
              </div>
              <h1 className="font-display text-3xl sm:text-4xl mt-5 text-[#1A231F]">Richiesta non disponibile</h1>
              <p className="text-[#5C7066] mt-2">Questo orario non può essere confermato. Scegli pure un altro momento, sarà un piacere trovarlo insieme.</p>
            </>
          ) : isPending ? (
            <>
              <div className="mx-auto h-20 w-20 rounded-full bg-[#D4A373]/15 grid place-items-center">
                <Hourglass size={38} className="text-[#D4A373]" strokeWidth={1.6} />
              </div>
              <h1 className="font-display text-3xl sm:text-4xl mt-5 text-[#1A231F]">Richiesta inviata</h1>
              <p className="text-[#5C7066] mt-2">Il tuo orario è riservato. Riceverai la conferma definitiva via email {settings?.avg_response_time}.</p>
            </>
          ) : (
            <>
              <div className="mx-auto h-20 w-20 rounded-full bg-[#8AA399]/15 grid place-items-center">
                <CheckCircle2 size={42} className="text-[#2D4A3E]" strokeWidth={1.6} />
              </div>
              <h1 className="font-display text-3xl sm:text-4xl mt-5 text-[#1A231F]">Prenotazione confermata</h1>
              <p className="text-[#5C7066] mt-2">Ti aspetto in studio. Risponderò a eventuali messaggi {settings?.avg_response_time}.</p>
            </>
          )}
        </div>

        <div className="card-soft p-6 mt-8 fade-up d1">
          <div className="overline mb-3">Riepilogo</div>
          <div className="space-y-3 text-[#1A231F]">
            <div className="flex items-start gap-3">
              <Calendar size={18} className="text-[#2D4A3E] mt-0.5"/>
              <div>
                <div className="font-medium">{fmtDate(appointment.start)}</div>
                <div className="text-sm text-[#5C7066]">Ore {fmtTime(appointment.start)} — {fmtTime(appointment.end)}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin size={18} className="text-[#2D4A3E] mt-0.5"/>
              <div>
                <div className="font-medium">{studio?.name}</div>
                <div className="text-sm text-[#5C7066]">{studio?.address}</div>
              </div>
            </div>
            <div className="pt-3 border-t border-[#E0E5E2] text-sm text-[#5C7066]">
              <div><span className="text-[#1A231F]">Motivo:</span> {appointment.reason}</div>
              <div className="mt-1"><span className="text-[#1A231F]">Paziente:</span> {appointment.first_name} {appointment.last_name}</div>
            </div>
          </div>
        </div>

        {!isRejected && !isPending && settings?.custom_message_confirm && (
          <div data-testid="confirm-custom-message" className="mt-5 rounded-xl border-l-4 border-[#D4A373] bg-[#FDF6E9] p-5 text-sm text-[#1A231F] whitespace-pre-wrap leading-relaxed fade-up d2">
            {settings.custom_message_confirm}
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-3 mt-6 fade-up d2">
          {!isPending && !isRejected && (
            <a
              data-testid="download-ics-btn"
              href={`${API}/appointments/${appointment.id}/ics`}
              className="inline-flex items-center justify-center gap-2 h-14 rounded-xl bg-[#2D4A3E] text-white hover:bg-[#1A2F26] transition-colors"
            >
              <Download size={18}/> Aggiungi al Calendario
            </a>
          )}
          {isRejected ? (
            <Link
              data-testid="confirm-rebook-btn"
              to="/prenota"
              className="inline-flex items-center justify-center gap-2 h-14 rounded-xl bg-[#2D4A3E] text-white hover:bg-[#1A2F26] transition-colors sm:col-span-2"
            >
              Scegli un altro orario
            </Link>
          ) : settings?.whatsapp_number && (
            <a
              data-testid="confirm-whatsapp-btn"
              href={buildWhatsAppUrl(settings.whatsapp_number, wmsg)}
              target="_blank" rel="noopener noreferrer"
              className={`inline-flex items-center justify-center gap-2 h-14 rounded-xl bg-[#25D366] text-white hover:bg-[#1EBE5A] transition-colors ${isPending ? "sm:col-span-2" : ""}`}
            >
              <MessageCircle size={18}/> Invia messaggio WhatsApp
            </a>
          )}
        </div>

        {!isRejected && !isPending && settings?.google_review_url && (
          <div className="mt-6 card-soft p-6 fade-up d3 text-center" data-testid="review-widget">
            <div className="text-2xl">⭐️⭐️⭐️⭐️⭐️</div>
            <div className="font-display text-lg text-[#1A231F] mt-2">Ti è piaciuto il servizio?</div>
            <p className="text-sm text-[#5C7066] mt-1">Una recensione su Google mi aiuta molto a farmi conoscere. Grazie!</p>
            <a
              data-testid="google-review-btn"
              href={settings.google_review_url}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 h-12 mt-4 px-6 rounded-xl bg-white border border-[#2D4A3E] text-[#2D4A3E] hover:bg-[#2D4A3E] hover:text-white transition-colors"
            >
              Lascia una recensione su Google
            </a>
          </div>
        )}

        {settings?.website_url && (
          <div className="mt-6 text-center fade-up d3">
            <a href={settings.website_url} target="_blank" rel="noopener noreferrer" data-testid="website-link" className="text-sm text-[#5C7066] hover:text-[#2D4A3E] underline underline-offset-4">
              Visita il sito {settings.website_url.replace(/^https?:\/\//,"").replace(/\/$/,"")}
            </a>
          </div>
        )}

        <div className="mt-8 text-center">
          <Link to="/" data-testid="back-home-confirm" className="inline-flex items-center gap-2 text-sm text-[#5C7066] hover:text-[#2D4A3E]">
            <Home size={14}/> Torna alla home
          </Link>
        </div>
      </main>
    </div>
  );
}
